import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { CatalogEnvelope, CatalogListEnvelope } from '../types/catalog-service.api.types';
import type { CatalogPage } from '../types/catalog-v2.types';
import type {
  CatalogCostApproverStatus,
  CostDecisionRequest,
  CostEstimateRead,
  RunStageErrors,
} from '../types/run-ledger.api.types';
import { toPage, toSearchParams, unwrap } from '../utils/envelope';

/** §93 — the run's stage errors, each with its code, verdict, message and next step. */
export async function getRunStageErrors(runRef: string): Promise<RunStageErrors> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.errors(runRef))
    .json<CatalogEnvelope<RunStageErrors>>();
  return unwrap(body);
}

/** §95 — the run's latest cost estimate; `null` when the gate never ran for it. */
export async function getRunCostEstimate(runRef: string): Promise<CostEstimateRead | null> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.costEstimate(runRef))
    .json<CatalogEnvelope<CostEstimateRead | null>>();
  // `data: null` is an answer here ("no estimate"), not a malformed envelope — no unwrap.
  return body.data ?? null;
}

/** §96 — re-ask a held gate's failed estimate; the answer is the re-priced (or still failed) gate. */
export async function retryCostEstimate(runRef: string): Promise<CostEstimateRead> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.operations.costEstimateRetry(runRef))
    .json<CatalogEnvelope<CostEstimateRead>>();
  return unwrap(body);
}

/** §95 — approve (optionally Vertex only) or reject the gate holding the run. */
export async function decideCost(
  runRef: string,
  request: CostDecisionRequest,
): Promise<CostEstimateRead> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.operations.costDecision(runRef), { json: request })
    .json<CatalogEnvelope<CostEstimateRead>>();
  return unwrap(body);
}

/** §95 — whether catalog-service counts the caller as a cost approver (and internal). */
export async function getCatalogCostApproverStatus(): Promise<CatalogCostApproverStatus> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.costApproverStatus())
    .json<CatalogEnvelope<CatalogCostApproverStatus>>();
  return unwrap(body);
}

/** §95 — estimates waiting for a decision in this subsidiary, oldest first. */
export async function getPendingCostEstimates(): Promise<CostEstimateRead[]> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.costEstimatesPending())
    .json<CatalogEnvelope<CostEstimateRead[]>>();
  return unwrap(body);
}

/** §95 — one page of decided estimates (the Cost approvals history), newest decision first. */
export async function getCostEstimateHistory(params: {
  status?: string | undefined;
  page: number;
  size: number;
}): Promise<CatalogPage<CostEstimateRead>> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.costEstimatesHistory(), {
      searchParams: toSearchParams(params),
    })
    .json<CatalogListEnvelope<CostEstimateRead>>();
  return toPage(body, (row) => row);
}
